import React from 'react';
import Tag, { TagType } from 'components/DesignSystem/Tag';
import TooltipWrapper from 'components/DesignSystem/TooltipWrapper';
import { IconType } from 'components/DesignSystem/Icon/icons';
import { EXTRA_STATE, GRAY3 } from 'constants/colors';

interface StatusTagConfig {
  type: TagType;
  label: string;
  icon?: IconType;
  backgroundColor?: string;
}

/**
 * Mapeia o status do contrato (string vinda do backend: 'Aprovado' |
 * 'Aguardando Aprovação' | 'Cancelado' | 'Vencido') para a aparência da pílula.
 *
 * - Aprovado / Cancelado: tons pastel do Design System (verde / vermelho).
 * - Aguardando Aprovação: pílula PREENCHIDA em laranja âmbar (EXTRA_STATE) com
 *   texto/ícone brancos (type 'primary') — destaque proposital, pois é o status
 *   que demanda ação. Rótulo encurtado p/ 'Aguardando' (completo no tooltip).
 * - Vencido: cinza neutro (GRAY3). O type 'completed' é usado APENAS para herdar
 *   o texto escuro do DS (a cor de fundo é sobrescrita por backgroundColor); a
 *   legibilidade depende de `.ds-tag-completed` manter o texto escuro.
 */
// tema: dados-inicio — fundo da pílula vira chip tingido no escuro (.ds-tag-data)
const STATUS_TAG_CONFIG: Record<string, StatusTagConfig> = {
  Aprovado: { type: 'completed', label: 'Aprovado', icon: 'checkmark' },
  'Aguardando Aprovação': {
    type: 'primary', label: 'Aguardando', icon: 'warning', backgroundColor: EXTRA_STATE,
  },
  Cancelado: { type: 'cancelled', label: 'Cancelado', icon: 'close' },
  Vencido: {
    type: 'completed', label: 'Vencido', icon: 'time', backgroundColor: GRAY3,
  },
};
// tema: dados-fim

export function contractStatusTagConfig(status: string): StatusTagConfig {
  return STATUS_TAG_CONFIG[status] ?? { type: 'primary', label: status };
}

export default function StatusTag({ status }: { status: string }) {
  if (!status) {
    return null;
  }

  const {
    type, label, icon, backgroundColor,
  } = contractStatusTagConfig(status);

  const tag = (
    <Tag type={type} icon={icon} backgroundColor={backgroundColor}>
      {label}
    </Tag>
  );

  // Quando o rótulo foi encurtado, expõe o texto completo via tooltip do DS.
  if (label !== status) {
    return <TooltipWrapper title={status}>{tag}</TooltipWrapper>;
  }

  return tag;
}
